import React from 'react';
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { summarizeDraft } from './draft-state';
import { CommissioningDraft } from './types';

export function ReviewAndProvision({
  draft,
  loading,
  onProvision,
  onRetry,
}: {
  draft: CommissioningDraft;
  loading: boolean;
  onProvision: () => void;
  onRetry: () => void;
}) {
  const summary = summarizeDraft(draft);
  return (
    <Stack spacing={2}>
      <Typography variant="h6">Review and provision</Typography>
      <Stack direction="row" gap={1} flexWrap="wrap">
        <Chip
          label={`${summary.counts.floors} Floor${summary.counts.floors === 1 ? '' : 's'}`}
        />
        <Chip
          label={`${summary.counts.units} Unit${summary.counts.units === 1 ? '' : 's'}`}
        />
        <Chip
          label={`${summary.counts.devices} Device${summary.counts.devices === 1 ? '' : 's'}`}
        />
        <Chip label={`${summary.counts.ignored} ignored`} />
      </Stack>
      <Typography variant="body2">
        {summary.newFloors} new Floors and {summary.newUnits} new Units will be
        created; {summary.existingFloors} existing Floors and{' '}
        {summary.existingUnits} existing Units are preserved. Unused proposed
        hierarchy will not be created.
      </Typography>
      {summary.errors.map((error) => (
        <Alert key={error} severity="warning">
          {error}
        </Alert>
      ))}
      {draft.status === 'complete' && (
        <Alert severity="success">Commissioning is complete.</Alert>
      )}
      {draft.status === 'awaitingConductor' && (
        <Alert severity="warning">
          Local hierarchy is provisioned. Conductor acknowledgement is pending;
          commissioning is not complete yet.
        </Alert>
      )}
      {draft.status === 'awaitingConductor' ? (
        <Button
          aria-label="Retry Conductor acknowledgement"
          disabled={loading}
          onClick={onRetry}
          variant="contained"
        >
          {loading ? (
            <CircularProgress size={20} />
          ) : (
            'Retry Conductor acknowledgement'
          )}
        </Button>
      ) : draft.status === 'draft' ? (
        <Button
          aria-label="Provision hierarchy"
          disabled={loading || !summary.canProvision}
          onClick={onProvision}
          variant="contained"
        >
          {loading ? <CircularProgress size={20} /> : 'Provision hierarchy'}
        </Button>
      ) : null}
    </Stack>
  );
}
